/**
 * useFamiliarisationTemplates
 * Hook for managing familiarisation checklist templates
 *
 * FT-115: CONSOLIDATED - This is now the SINGLE SOURCE OF TRUTH for all familiarisation config.
 * Previously scattered across 4 storage keys:
 * - familiarisation_checklist_templates (templates) ✅ kept
 * - familiarisationSettings (validity period) → moved into templatesData.settings
 * - familiarisationCustomizations (editedItems, deletedItems) → moved into templatesData.customizations
 * - customFamiliarisationItems (custom items) → moved into templatesData.customizations.customItems
 *
 * All edits now go through setTemplatesData, syncs via single key.
 */

import { useEffect, useCallback, useMemo } from 'react';
import { useStorageState } from './useStorageState';
import { useVessel } from '../contexts/VesselContext';
import { getOrgId } from '../utils/getOrgId';
import {
  migrateFamiliarisationToV2,
  getTemplateById,
  getDefaultTemplate,
  getTemplatesForPosition,
  getTemplateItemCount,
  validateTemplate,
  generateTemplateId,
  buildDefaultTemplate,
  buildContractorTemplate
} from '../utils/familiarisationTemplateHelpers';

const EMPTY_TEMPLATES_DATA = {
  version: 2,  // FT-115: Bumped version for consolidated structure
  lastUpdated: null,
  templates: [],
  // FT-115: Consolidated settings (was familiarisationSettings)
  settings: {
    validityPeriodMonths: 12,
    paperMode: false
  },
  // FT-115: Consolidated customizations (was familiarisationCustomizations + customFamiliarisationItems)
  customizations: {
    editedItems: {},
    deletedItems: [],
    customItems: []
  }
};

export function useFamiliarisationTemplates() {
  const { currentVessel } = useVessel();
  const vesselId = currentVessel?.id;

  const { value: templatesData, setValue: setTemplatesData } = useStorageState(
    'familiarisation_checklist_templates',
    EMPTY_TEMPLATES_DATA
  );

  // FT-115: Migrate legacy unscoped keys to consolidated structure
  useEffect(() => {
    if (!vesselId) return;

    // Check for legacy unscoped keys that need migration
    const legacyCustomizationsKey = 'familiarisationCustomizations';
    const legacyCustomItemsKey = 'customFamiliarisationItems';

    // FT-115 follow-up: Include orgId in migration flag for multi-tenant support
    const orgId = getOrgId();
    const migrationFlag = orgId && orgId !== 'default-org'
      ? `familiarisation_v2_consolidated_${orgId}_${vesselId}`
      : `familiarisation_v2_consolidated_${vesselId}`;

    // Skip if already migrated
    if (localStorage.getItem(migrationFlag)) return;

    const legacyCustomizations = localStorage.getItem(legacyCustomizationsKey);
    const legacyCustomItems = localStorage.getItem(legacyCustomItemsKey);

    if (legacyCustomizations || legacyCustomItems) {
      console.log('[useFamiliarisationTemplates] FT-115: Migrating legacy unscoped keys...');

      let customizations = { editedItems: {}, deletedItems: [], customItems: [] };

      if (legacyCustomizations) {
        try {
          const parsed = JSON.parse(legacyCustomizations);
          customizations.editedItems = parsed.editedItems || {};
          customizations.deletedItems = parsed.deletedItems || [];
          customizations.customItems = parsed.customItems || [];
          if (parsed.paperMode !== undefined) {
            customizations.paperMode = parsed.paperMode;
          }
        } catch (e) {
          console.warn('[useFamiliarisationTemplates] Failed to parse legacy customizations', e);
        }
      }

      if (legacyCustomItems) {
        try {
          const parsed = JSON.parse(legacyCustomItems);
          // Merge with any custom items from customizations
          if (Array.isArray(parsed) && parsed.length > 0) {
            const existingIds = new Set(customizations.customItems.map(i => i.id));
            parsed.forEach(item => {
              if (!existingIds.has(item.id)) {
                customizations.customItems.push(item);
              }
            });
          }
        } catch (e) {
          console.warn('[useFamiliarisationTemplates] Failed to parse legacy customItems', e);
        }
      }

      // Merge into templatesData
      setTemplatesData(prev => ({
        ...prev,
        version: 2,
        lastUpdated: new Date().toISOString(),
        customizations: {
          ...prev.customizations,
          ...customizations
        },
        settings: {
          ...prev.settings,
          paperMode: customizations.paperMode ?? prev.settings?.paperMode ?? false
        }
      }));

      // Clean up legacy keys (remove them to prevent re-migration)
      localStorage.removeItem(legacyCustomizationsKey);
      localStorage.removeItem(legacyCustomItemsKey);

      // Mark migration complete
      localStorage.setItem(migrationFlag, new Date().toISOString());
      console.log('[useFamiliarisationTemplates] FT-115: Legacy migration complete');
    } else {
      // No legacy keys to migrate, just mark as done
      localStorage.setItem(migrationFlag, new Date().toISOString());
    }
  }, [vesselId, setTemplatesData]);

  // Run template migration on first load if needed
  useEffect(() => {
    if (!vesselId) return;

    // Check if we have templates
    if (!templatesData?.templates || templatesData.templates.length === 0) {
      console.log('[useFamiliarisationTemplates] No templates found, running migration...');
      const migratedData = migrateFamiliarisationToV2(vesselId);

      if (migratedData) {
        setTemplatesData(migratedData);
      } else {
        // Migration already done but data not loaded - force reload
        const stored = localStorage.getItem(`familiarisation_checklist_templates_${vesselId}`);
        if (stored) {
          try {
            setTemplatesData(JSON.parse(stored));
          } catch (e) {
            console.error('[useFamiliarisationTemplates] Failed to parse stored templates', e);
          }
        }
      }
    }
  }, [vesselId, templatesData?.templates?.length, setTemplatesData]);

  // Get all templates
  const templates = useMemo(() => {
    return templatesData?.templates || [];
  }, [templatesData]);

  // Add a new template
  const addTemplate = useCallback((templateData) => {
    const newTemplate = {
      ...templateData,
      id: templateData.id || generateTemplateId(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    const validation = validateTemplate(newTemplate);
    if (!validation.valid) {
      console.error('[useFamiliarisationTemplates] Invalid template:', validation.errors);
      return { success: false, errors: validation.errors };
    }

    setTemplatesData(prev => ({
      ...prev,
      lastUpdated: new Date().toISOString(),
      templates: [...(prev.templates || []), newTemplate]
    }));

    return { success: true, template: newTemplate };
  }, [setTemplatesData]);

  // Update an existing template
  const updateTemplate = useCallback((templateId, updates) => {
    setTemplatesData(prev => ({
      ...prev,
      lastUpdated: new Date().toISOString(),
      templates: (prev.templates || []).map(t =>
        t.id === templateId
          ? { ...t, ...updates, updatedAt: new Date().toISOString() }
          : t
      )
    }));
  }, [setTemplatesData]);

  // Delete a template (only non-system templates)
  const deleteTemplate = useCallback((templateId) => {
    const template = getTemplateById(templatesData, templateId);

    if (template?.isSystem && template?.isDefault) {
      console.warn('[useFamiliarisationTemplates] Cannot delete default system template');
      return { success: false, error: 'Cannot delete default template' };
    }

    setTemplatesData(prev => ({
      ...prev,
      lastUpdated: new Date().toISOString(),
      templates: (prev.templates || []).filter(t => t.id !== templateId)
    }));

    return { success: true };
  }, [templatesData, setTemplatesData]);

  // Duplicate a template
  const duplicateTemplate = useCallback((templateId, newName) => {
    const original = getTemplateById(templatesData, templateId);
    if (!original) {
      return { success: false, error: 'Template not found' };
    }

    const duplicate = {
      ...original,
      id: generateTemplateId(),
      name: newName || `${original.name} (Copy)`,
      isDefault: false,
      isSystem: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    return addTemplate(duplicate);
  }, [templatesData, addTemplate]);

  // Set a template as default
  const setDefaultTemplate = useCallback((templateId) => {
    setTemplatesData(prev => ({
      ...prev,
      lastUpdated: new Date().toISOString(),
      templates: (prev.templates || []).map(t => ({
        ...t,
        isDefault: t.id === templateId
      }))
    }));
  }, [setTemplatesData]);

  // ============================================
  // FT-115: SETTINGS MANAGEMENT
  // ============================================

  // Get current settings
  const settings = useMemo(() => ({
    validityPeriodMonths: templatesData?.settings?.validityPeriodMonths ?? 12,
    paperMode: templatesData?.settings?.paperMode ?? false
  }), [templatesData?.settings]);

  // Update settings (validity period, paper mode)
  const updateSettings = useCallback((newSettings) => {
    setTemplatesData(prev => ({
      ...prev,
      lastUpdated: new Date().toISOString(),
      settings: {
        ...prev.settings,
        ...newSettings
      }
    }));
  }, [setTemplatesData]);

  // ============================================
  // FT-115: CUSTOMIZATIONS MANAGEMENT
  // ============================================

  // Get current customizations
  const customizations = useMemo(() => ({
    editedItems: templatesData?.customizations?.editedItems ?? {},
    deletedItems: templatesData?.customizations?.deletedItems ?? [],
    customItems: templatesData?.customizations?.customItems ?? []
  }), [templatesData?.customizations]);

  // Update customizations (all at once - for save button)
  const updateCustomizations = useCallback((newCustomizations) => {
    setTemplatesData(prev => ({
      ...prev,
      lastUpdated: new Date().toISOString(),
      customizations: {
        ...prev.customizations,
        ...newCustomizations
      }
    }));
  }, [setTemplatesData]);

  // Add a custom item
  const addCustomItem = useCallback((item) => {
    const newItem = {
      id: item.id || `custom_${Date.now()}`,
      ...item,
      isCustom: true,
      createdAt: new Date().toISOString()
    };
    setTemplatesData(prev => ({
      ...prev,
      lastUpdated: new Date().toISOString(),
      customizations: {
        ...prev.customizations,
        customItems: [...(prev.customizations?.customItems || []), newItem]
      }
    }));
    return newItem;
  }, [setTemplatesData]);

  // Update a custom item
  const updateCustomItem = useCallback((itemId, updates) => {
    setTemplatesData(prev => ({
      ...prev,
      lastUpdated: new Date().toISOString(),
      customizations: {
        ...prev.customizations,
        customItems: (prev.customizations?.customItems || []).map(item =>
          item.id === itemId ? { ...item, ...updates } : item
        )
      }
    }));
  }, [setTemplatesData]);

  // Delete a custom item
  const deleteCustomItem = useCallback((itemId) => {
    setTemplatesData(prev => ({
      ...prev,
      lastUpdated: new Date().toISOString(),
      customizations: {
        ...prev.customizations,
        customItems: (prev.customizations?.customItems || []).filter(item => item.id !== itemId)
      }
    }));
  }, [setTemplatesData]);

  // Edit a standard item (store override)
  const editStandardItem = useCallback((itemId, newText) => {
    setTemplatesData(prev => ({
      ...prev,
      lastUpdated: new Date().toISOString(),
      customizations: {
        ...prev.customizations,
        editedItems: {
          ...prev.customizations?.editedItems,
          [itemId]: { text: newText, topic: newText }
        }
      }
    }));
  }, [setTemplatesData]);

  // Delete a standard item (mark as deleted)
  const deleteStandardItem = useCallback((itemId) => {
    setTemplatesData(prev => ({
      ...prev,
      lastUpdated: new Date().toISOString(),
      customizations: {
        ...prev.customizations,
        deletedItems: [...new Set([...(prev.customizations?.deletedItems || []), itemId])]
      }
    }));
  }, [setTemplatesData]);

  // Restore a deleted standard item
  const restoreStandardItem = useCallback((itemId) => {
    setTemplatesData(prev => ({
      ...prev,
      lastUpdated: new Date().toISOString(),
      customizations: {
        ...prev.customizations,
        deletedItems: (prev.customizations?.deletedItems || []).filter(id => id !== itemId)
      }
    }));
  }, [setTemplatesData]);

  // Reset all customizations
  const resetCustomizations = useCallback(() => {
    setTemplatesData(prev => ({
      ...prev,
      lastUpdated: new Date().toISOString(),
      customizations: {
        editedItems: {},
        deletedItems: [],
        customItems: []
      }
    }));
  }, [setTemplatesData]);

  return {
    // Data
    templates,
    templatesData,
    isLoaded: templates.length > 0,

    // Template CRUD
    addTemplate,
    updateTemplate,
    deleteTemplate,
    duplicateTemplate,
    setDefaultTemplate,

    // FT-115: Settings (consolidated - was familiarisationSettings)
    settings,
    updateSettings,

    // FT-115: Customizations (consolidated - was familiarisationCustomizations + customFamiliarisationItems)
    customizations,
    updateCustomizations,
    addCustomItem,
    updateCustomItem,
    deleteCustomItem,
    editStandardItem,
    deleteStandardItem,
    restoreStandardItem,
    resetCustomizations,

    // Utility functions
    getTemplateById: (id) => getTemplateById(templatesData, id),
    getDefaultTemplate: () => getDefaultTemplate(templatesData),
    getTemplatesForPosition: (position) => getTemplatesForPosition(templatesData, position),
    getTemplateItemCount
  };
}

export default useFamiliarisationTemplates;
